import { useState, useEffect } from 'react';
import { employeeAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Search, Loader2, Mail, User, Hash, Briefcase, MapPin } from 'lucide-react';

const EmployeeDirectory = () => {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const pageSize = 20;

    useEffect(() => {
        fetchDirectory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, search]);

    const fetchDirectory = async () => {
        try {
            setLoading(true);
            const response = await employeeAPI.getDirectory({
                page,
                page_size: pageSize,
                search: search || undefined,
            });
            setEmployees(response.data.employees);
            setTotal(response.data.total);
        } catch (error) {
            toast.error('Failed to fetch employee directory');
        } finally {
            setLoading(false);
        }
    };

    const totalPages = Math.ceil(total / pageSize);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold tracking-tight">Employee Directory</h1>
            </div>

            <Card className="border-border shadow-sm">
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                        <Search className="h-5 w-5 text-muted-foreground" />
                        Search Directory
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search by name, email or ID..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                            className="pl-10 h-11"
                        />
                    </div>
                </CardContent>
            </Card>

            <Card className="border-border shadow-sm overflow-hidden">
                <Table>
                    <TableHeader className="bg-muted/50">
                        <TableRow>
                            <TableHead className="w-[80px]">ID</TableHead>
                            <TableHead>Employee Name</TableHead>
                            <TableHead>Email Address</TableHead>
                            <TableHead>Department</TableHead>
                            <TableHead>Designation</TableHead>
                            <TableHead>Location</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && employees.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className="h-48 text-center">
                                    <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
                                        <Loader2 className="h-8 w-8 animate-spin" />
                                        <p>Loading directory...</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : employees.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className="h-48 text-center">
                                    <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
                                        <Search className="h-8 w-8" />
                                        <p>No employees found</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            employees.map((emp) => (
                                <TableRow key={emp.id} className="hover:bg-muted/30 transition-colors">
                                    <TableCell className="font-mono text-xs font-medium">
                                        {emp.employee_code}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xs shrink-0">
                                                {emp.first_name[0]}{emp.last_name[0]}
                                            </div>
                                            <span className="font-medium">{emp.first_name} {emp.last_name}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <Mail className="h-3.5 w-3.5" />
                                            {emp.email}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-sm">
                                        {emp.department || '-'}
                                    </TableCell>
                                    <TableCell className="text-sm">
                                        {emp.designation || '-'}
                                    </TableCell>
                                    <TableCell className="text-sm">
                                        <div className="flex items-center gap-1.5 text-muted-foreground">
                                            <MapPin className="h-3.5 w-3.5" />
                                            {emp.work_location || '-'}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>

                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-4 bg-muted/20 border-t">
                        <div className="text-sm text-muted-foreground">
                            Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total} employees
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                disabled={page === 1 || loading}
                                onClick={() => setPage(page - 1)}
                                className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2"
                            >
                                Previous
                            </button>
                            <button
                                disabled={page === totalPages || loading}
                                onClick={() => setPage(page + 1)}
                                className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default EmployeeDirectory;
